import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Dumbbell,
  Eye,
  Hourglass,
  Lock,
  RotateCcw,
  Send,
  Timer,
  Trophy,
  XCircle,
} from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { ApiError, api } from '@/lib/api';
import type { AttemptQuestion, AttemptResult, AttemptStart, CorrectionItem, QuizOverview } from '@/types/api';
import { cn, formatPercent } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { QuestionInput } from '@/features/quiz/QuestionInput';
import { furthestReachable, initialDraft, isAnswered, toPayload, type AnswerDraft } from '@/features/quiz/answers';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  LoadingState,
  Modal,
  Progress,
  useToast,
} from '@/components/ui';

type Stage = 'overview' | 'running' | 'result';

export function QuizPage() {
  const { quizId = '' } = useParams();
  const { t, language, pick } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [stage, setStage] = useState<Stage>('overview');
  const [attempt, setAttempt] = useState<AttemptStart | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerDraft>>({});
  const [index, setIndex] = useState(0);
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [focusLosses, setFocusLosses] = useState(0);

  const overview = useQuery({
    queryKey: ['quiz', quizId],
    queryFn: () => api.get<QuizOverview>(`/quizzes/${quizId}`),
    enabled: Boolean(quizId),
  });

  const submit = useMutation({
    mutationFn: (attemptId: string) =>
      api.post<AttemptResult>(`/quizzes/attempts/${attemptId}/submit`, {
        answers: (attempt?.questions ?? []).map((question) =>
          toPayload(question, answers[question.id] ?? initialDraft(question)),
        ),
      }),
    onSuccess: (data) => {
      setResult(data);
      setStage('result');
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['quiz', quizId] });
      void queryClient.invalidateQueries({ queryKey: ['progress', 'overview'] });
    },
    onError: (cause) => {
      setConfirming(false);
      toast.error(cause instanceof ApiError ? cause.localized() : t('errors.generic'));
      if (cause instanceof ApiError && cause.code === 'QUIZ_ATTEMPT_EXPIRED') {
        setStage('overview');
        void overview.refetch();
      }
    },
  });

  const start = useMutation({
    mutationFn: (practice: boolean) => api.post<AttemptStart>(`/quizzes/${quizId}/attempts`, { practice }),
    onSuccess: (data) => {
      setAttempt(data);
      // Chaque question part d'un brouillon, ce qui garantit qu'elle est
      // transmise meme laissee vide.
      setAnswers(Object.fromEntries(data.questions.map((question) => [question.id, initialDraft(question)])));
      setIndex(0);
      setResult(null);
      setStage('running');
    },
    onError: (cause) => toast.error(cause instanceof ApiError ? cause.localized() : t('errors.generic')),
  });

  const submitAttempt = useCallback(() => {
    if (attempt && !submit.isPending) submit.mutate(attempt.attempt.id);
  }, [attempt, submit]);

  // Minuteur : decompte et soumission automatique a l'expiration.
  useEffect(() => {
    if (stage !== 'running' || !attempt?.attempt.expiresAt) {
      setRemaining(null);
      return undefined;
    }

    const expiresAt = new Date(attempt.attempt.expiresAt).getTime();
    const tick = () => {
      const seconds = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setRemaining(seconds);
      if (seconds === 0) submitAttempt();
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [stage, attempt, submitAttempt]);

  // Surveillance : chaque passage en arriere-plan est signale a l'API et
  // reste visible du correcteur. | Proctoring: each blur is reported.
  useEffect(() => {
    if (stage !== 'running' || !attempt?.quiz.proctored) return undefined;

    const attemptId = attempt.attempt.id;
    const onHidden = () => {
      if (document.visibilityState !== 'hidden') return;
      setFocusLosses((count) => count + 1);
      void api.post(`/quizzes/attempts/${attemptId}/focus-loss`).catch(() => {
        /* le signal est indicatif : une perte reseau ne bloque pas l'epreuve */
      });
    };

    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [stage, attempt]);

  const questions = useMemo(() => attempt?.questions ?? [], [attempt]);
  const current = questions[index];

  const unanswered = useMemo(
    () => questions.filter((question) => !isAnswered(question, answers[question.id])).length,
    [questions, answers],
  );

  // L'epreuve avance question par question : on ne depasse pas la premiere
  // restee sans reponse.
  const reachable = useMemo(() => furthestReachable(questions, answers), [questions, answers]);

  if (overview.isLoading) return <LoadingState />;
  if (overview.isError || !overview.data) return <ErrorState onRetry={() => void overview.refetch()} />;

  const { quiz, attempts, attemptsRemaining, bestScorePercent, passed } = overview.data;

  // Une enquete de satisfaction n'a ni note ni seuil de reussite.
  const isSurvey = quiz.kind === 'SURVEY';

  // --- Etape 1 : presentation de l'evaluation -------------------------------
  if (stage === 'overview') {
    return (
      <div className="mx-auto max-w-3xl">
        <PageHeader title={pick(quiz, 'title')} description={pick(quiz, 'description') || undefined} />

        <Card>
          <CardBody className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <InfoTile label={t('quiz.questionsCount', { count: quiz._count?.questions ?? 0 })} value={String(quiz._count?.questions ?? 0)} />
              {isSurvey ? (
                <InfoTile label={t('quiz.anonymity')} value={t('quiz.anonymous')} />
              ) : (
                <InfoTile label={t('quiz.passingScore')} value={`${quiz.passingScore}%`} />
              )}
              <InfoTile
                label={t('quiz.timeLimit')}
                value={quiz.timeLimitMinutes ? `${quiz.timeLimitMinutes} min` : '—'}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={quiz.kind === 'FINAL_EXAM' ? 'accent' : 'primary'}>{t(`quizKinds.${quiz.kind}`)}</Badge>
              {isSurvey ? null : (
                <Badge tone={attemptsRemaining > 0 ? 'neutral' : 'danger'}>
                  {t('quiz.attemptsRemaining')} : {attemptsRemaining} / {quiz.maxAttempts}
                </Badge>
              )}
              {bestScorePercent !== null && !isSurvey ? (
                <Badge tone={passed ? 'success' : 'warning'}>
                  {t('quiz.bestScore')} : {formatPercent(bestScorePercent, language, 0)}
                </Badge>
              ) : null}
              {quiz.drawSize > 0 ? <Badge tone="neutral">{t('quiz.drawnQuestions', { count: quiz.drawSize })}</Badge> : null}
              {quiz.proctored ? (
                <Badge tone="warning">
                  <Eye className="h-3.5 w-3.5" aria-hidden />
                  {t('quiz.proctored')}
                </Badge>
              ) : null}
            </div>

            {attempts.length > 0 ? (
              <div className="rounded-xl border border-border">
                <ul className="divide-y divide-border">
                  {attempts
                    .filter((item) => item.status === 'SUBMITTED' && !item.isPractice)
                    .map((item) => {
                      const percent = item.maxScore > 0 ? (item.score / item.maxScore) * 100 : 0;
                      return (
                        <li key={item.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                          <span className="text-ink-muted">
                            {t('quiz.attemptsUsed', { used: item.attemptNumber, max: quiz.maxAttempts })}
                          </span>
                          <Badge tone={item.passed ? 'success' : 'danger'}>{formatPercent(percent, language, 0)}</Badge>
                        </li>
                      );
                    })}
                </ul>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                onClick={() => start.mutate(false)}
                loading={start.isPending}
                disabled={attemptsRemaining <= 0}
                icon={<ClipboardCheck className="h-4 w-4" />}
              >
                {overview.data.openAttempt ? t('quiz.resume') : t('quiz.start')}
              </Button>
              {quiz.allowPractice && !isSurvey && !overview.data.openAttempt ? (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => start.mutate(true)}
                  loading={start.isPending}
                  icon={<Dumbbell className="h-4 w-4" />}
                >
                  {t('quiz.practice')}
                </Button>
              ) : null}
              <Button variant="ghost" onClick={() => navigate(-1)} icon={<ArrowLeft className="h-4 w-4" />}>
                {t('common.back')}
              </Button>
            </div>

            {quiz.allowPractice && !isSurvey ? <p className="text-xs text-ink-muted">{t('quiz.practiceHint')}</p> : null}

            {overview.data.awaitingGrading ? (
              <p className="flex items-center gap-2 rounded-xl bg-warning-soft px-4 py-3 text-sm text-warning">
                <Hourglass className="h-4 w-4 shrink-0" aria-hidden />
                {t('quiz.awaitingGrading')}
              </p>
            ) : null}

            {attemptsRemaining <= 0 ? (
              <p className="flex items-center gap-2 rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                {t('quiz.noAttemptsLeft')}
              </p>
            ) : null}
          </CardBody>
        </Card>
      </div>
    );
  }

  // --- Etape 3 : resultat et correction -------------------------------------
  if (stage === 'result' && result) {
    // Une enquete n'a ni note ni corrige : on remercie simplement l'agent.
    if (isSurvey) {
      return (
        <div className="mx-auto max-w-3xl">
          <Card className="border-success/40">
            <CardBody className="flex flex-col items-center gap-4 py-10 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-success-soft text-success">
                <CheckCircle2 className="h-8 w-8" aria-hidden />
              </span>
              <div>
                <h1 className="font-display text-2xl font-bold text-ink">{t('quiz.surveyThanksTitle')}</h1>
                <p className="mt-1 max-w-md text-sm text-ink-muted">{t('quiz.surveyThanksBody')}</p>
              </div>
              <Link to={quiz.sessionId ? `/learn/sessions/${quiz.sessionId}` : '/learn'}>
                <Button variant="outline">{quiz.sessionId ? t('quiz.backToSession') : t('learn.backToPath')}</Button>
              </Link>
            </CardBody>
          </Card>
        </div>
      );
    }

    return (
      <div className="mx-auto max-w-3xl">
        <Card
          className={cn(
            'mb-6 overflow-hidden',
            result.awaitingGrading ? 'border-warning/40' : result.passed ? 'border-success/40' : 'border-danger/40',
          )}
        >
          <CardBody className="flex flex-col items-center gap-4 py-10 text-center">
            <span
              className={cn(
                'flex h-16 w-16 items-center justify-center rounded-3xl',
                result.awaitingGrading
                  ? 'bg-warning-soft text-warning'
                  : result.passed
                    ? 'bg-success-soft text-success'
                    : 'bg-danger-soft text-danger',
              )}
            >
              {result.awaitingGrading ? (
                <Hourglass className="h-8 w-8" aria-hidden />
              ) : result.passed ? (
                <Trophy className="h-8 w-8" aria-hidden />
              ) : (
                <XCircle className="h-8 w-8" aria-hidden />
              )}
            </span>
            <div>
              <h1 className="font-display text-2xl font-bold text-ink">
                {result.awaitingGrading ? t('quiz.awaitingGradingTitle') : result.passed ? t('quiz.passed') : t('quiz.failed')}
              </h1>
              <p className="mt-1 text-sm text-ink-muted">
                {t('quiz.yourScore')} : <strong className="text-ink">{formatPercent(result.scorePercent, language)}</strong>
                {' · '}
                {t('quiz.passingScore')} : {quiz.passingScore}%
              </p>
            </div>
            <Progress value={result.scorePercent} tone={result.passed ? 'success' : 'primary'} className="max-w-xs" />

            {result.awaitingGrading ? (
              <p className="max-w-md rounded-xl bg-warning-soft px-4 py-3 text-sm text-warning">
                {t('quiz.awaitingGrading')}
              </p>
            ) : null}

            <div className="flex flex-wrap justify-center gap-2 pt-2">
              {!result.passed && attemptsRemaining > 1 ? (
                <Button onClick={() => start.mutate(false)} loading={start.isPending} icon={<RotateCcw className="h-4 w-4" />}>
                  {t('quiz.retryQuiz')}
                </Button>
              ) : null}
              <Link to={quiz.sessionId ? `/learn/sessions/${quiz.sessionId}` : '/learn'}>
                <Button variant="outline">{quiz.sessionId ? t('quiz.backToSession') : t('learn.backToPath')}</Button>
              </Link>
            </div>
          </CardBody>
        </Card>

        {result.correction ? (
          <Card>
            <CardHeader title={t('quiz.correction')} />
            <CardBody className="space-y-4">
              {result.correction.map((item, position) => {
                const question = questions.find((entry) => entry.id === item.questionId);
                return (
                  <div
                    key={item.questionId}
                    className={cn(
                      'rounded-xl border p-4',
                      item.awaitingGrading
                        ? 'border-warning/30 bg-warning-soft/40'
                        : item.isCorrect
                          ? 'border-success/30 bg-success-soft/40'
                          : 'border-danger/30 bg-danger-soft/40',
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {item.awaitingGrading ? (
                        <Hourglass className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
                      ) : item.isCorrect ? (
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
                      ) : (
                        <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
                      )}
                      <div className="min-w-0 flex-1 space-y-2">
                        <p className="font-medium text-ink">
                          {position + 1}. {language === 'en' ? item.textEn : item.textFr}
                        </p>

                        <CorrectionDetail item={item} question={question} />

                        {(language === 'en' ? item.explanationEn : item.explanationFr) ? (
                          <p className="rounded-lg bg-surface px-3 py-2 text-sm text-ink-muted">
                            <strong className="text-ink">{t('quiz.explanation')} : </strong>
                            {language === 'en' ? item.explanationEn : item.explanationFr}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardBody>
          </Card>
        ) : null}
      </div>
    );
  }

  // --- Etape 2 : passation ---------------------------------------------------
  if (!current) return <LoadingState />;

  const draft = answers[current.id] ?? initialDraft(current);
  const currentAnswered = isAnswered(current, draft);
  const lowTime = remaining !== null && remaining <= 120;
  const practice = attempt?.attempt.isPractice ?? false;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate font-display text-lg font-semibold text-ink">{pick(quiz, 'title')}</h1>
          <p className="text-sm text-ink-muted">{t('quiz.question', { current: index + 1, total: questions.length })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {practice ? (
            <Badge tone="info">
              <Dumbbell className="h-3.5 w-3.5" aria-hidden />
              {t('quiz.practiceMode')}
            </Badge>
          ) : null}
          {attempt?.quiz.proctored ? (
            <Badge tone={focusLosses > 0 ? 'danger' : 'warning'}>
              <Eye className="h-3.5 w-3.5" aria-hidden />
              {focusLosses > 0 ? t('quiz.focusLosses', { count: focusLosses }) : t('quiz.proctored')}
            </Badge>
          ) : null}
          {remaining !== null ? (
            <Badge tone={lowTime ? 'danger' : 'neutral'} className="text-sm tabular-nums">
              <Timer className="h-4 w-4" aria-hidden />
              {String(Math.floor(remaining / 60)).padStart(2, '0')}:{String(remaining % 60).padStart(2, '0')}
            </Badge>
          ) : null}
        </div>
      </div>

      <Progress value={((index + 1) / questions.length) * 100} size="sm" className="mb-5" />

      <Card className="mb-5">
        <CardBody className="space-y-5">
          <div>
            <p className="mb-1.5 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-ink-subtle">
              <span>{t(`quiz.instructions.${current.type}`)}</span>
              <span>·</span>
              <span>{t('quiz.pointsCount', { count: current.points })}</span>
              {current.markingMode === 'PARTIAL' ? (
                <>
                  <span>·</span>
                  <span>{t('quiz.partialCredit')}</span>
                </>
              ) : null}
            </p>
            <p className="font-display text-lg font-semibold leading-snug text-ink">{pick(current, 'text')}</p>
          </div>

          <QuestionInput
            question={current}
            draft={draft}
            onChange={(next) =>
              setAnswers((previous) => ({ ...previous, [current.id]: { ...next, touched: true } }))
            }
          />
        </CardBody>
      </Card>

      {currentAnswered ? null : (
        <p
          id="quiz-answer-required"
          className="mb-3 flex items-start gap-2 rounded-xl bg-surface-muted px-4 py-3 text-sm text-ink-muted"
        >
          <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {t(current.type === 'ORDERING' ? 'quiz.orderBeforeNext' : 'quiz.answerBeforeNext')}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          disabled={index === 0}
          onClick={() => setIndex((value) => value - 1)}
          icon={<ArrowLeft className="h-4 w-4" />}
        >
          {t('common.previous')}
        </Button>

        <div className="flex items-center gap-2">
          {index < questions.length - 1 ? (
            <Button
              disabled={!currentAnswered}
              aria-describedby={currentAnswered ? undefined : 'quiz-answer-required'}
              onClick={() => setIndex((value) => value + 1)}
            >
              {t('common.next')}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          ) : (
            <Button
              disabled={!currentAnswered}
              aria-describedby={currentAnswered ? undefined : 'quiz-answer-required'}
              onClick={() => setConfirming(true)}
              icon={<Send className="h-4 w-4" />}
            >
              {t('quiz.submit')}
            </Button>
          )}
        </div>
      </div>

      {/* Navigation rapide entre les questions */}
      <div className="mt-5 flex flex-wrap gap-1.5">
        {questions.map((question, position) => {
          const done = isAnswered(question, answers[question.id]);
          // Une question encore inaccessible reste visible, mais fermee.
          const locked = position > reachable;
          return (
            <button
              key={question.id}
              type="button"
              disabled={locked}
              onClick={() => setIndex(position)}
              title={locked ? t('quiz.lockedQuestion') : undefined}
              aria-label={
                locked
                  ? `${t('quiz.question', { current: position + 1, total: questions.length })} — ${t('quiz.lockedQuestion')}`
                  : t('quiz.question', { current: position + 1, total: questions.length })
              }
              className={cn(
                'h-8 w-8 rounded-lg text-xs font-semibold transition-colors',
                position === index
                  ? 'bg-primary text-primary-ink'
                  : locked
                    ? 'cursor-not-allowed bg-surface-muted text-ink-subtle/50'
                    : done
                      ? 'bg-primary-soft text-primary'
                      : 'bg-surface-muted text-ink-subtle hover:bg-border',
              )}
            >
              {position + 1}
            </button>
          );
        })}
      </div>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t('quiz.confirmSubmit')}
        description={t('quiz.confirmSubmitBody')}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submitAttempt} loading={submit.isPending} icon={<Send className="h-4 w-4" />}>
              {submit.isPending ? t('quiz.submitting') : t('quiz.submit')}
            </Button>
          </>
        }
      >
        {unanswered > 0 ? (
          <p className="flex items-start gap-2 rounded-xl bg-warning-soft px-4 py-3 text-sm text-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {t('quiz.unansweredWarning', { count: unanswered })}
          </p>
        ) : (
          <p className="text-sm text-ink-muted">{t('quiz.confirmSubmitBody')}</p>
        )}
      </Modal>
    </div>
  );
}

/**
 * Corrige d'une question, presente selon son type : choix coches, ordre
 * attendu, appariements, reponses acceptees ou copie libre.
 */
function CorrectionDetail({ item, question }: { item: CorrectionItem; question: AttemptQuestion | undefined }) {
  const { t, pick, language } = useLanguage();

  const label = (choiceId: string): string => {
    const choice = question?.choices.find((entry) => entry.id === choiceId);
    const target = question?.matches?.find((entry) => entry.id === choiceId);
    return pick(choice ?? target, 'text') || choiceId;
  };

  if (item.type === 'FREE_TEXT') {
    return (
      <div className="space-y-2 text-sm">
        <p className="whitespace-pre-wrap rounded-lg bg-surface px-3 py-2 text-ink">{item.textAnswer}</p>
        <p className="text-ink-muted">
          {item.awaitingGrading
            ? t('quiz.awaitingGrading')
            : t('quiz.pointsAwarded', { awarded: item.pointsAwarded, total: item.points })}
        </p>
      </div>
    );
  }

  if (item.type === 'FILL_BLANK') {
    return (
      <div className="space-y-1.5 text-sm">
        <p className={cn('rounded-lg px-3 py-2', item.isCorrect ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger')}>
          {item.textAnswer || t('quiz.noAnswer')}
        </p>
        {!item.isCorrect && item.acceptedAnswers.length > 0 ? (
          <p className="text-ink-muted">
            <strong className="text-ink">{t('quiz.acceptedAnswers')} : </strong>
            {item.acceptedAnswers.join(' · ')}
          </p>
        ) : null}
      </div>
    );
  }

  if (item.type === 'ORDERING') {
    return (
      <ol className="space-y-1 text-sm">
        {item.orderedChoiceIds.map((choiceId, position) => {
          const wellPlaced = item.correctOrder[position] === choiceId;
          return (
            <li
              key={choiceId}
              className={cn(
                'flex items-center gap-2 rounded-lg px-2.5 py-1.5',
                wellPlaced ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
              )}
            >
              <span className="text-xs tabular-nums">{position + 1}.</span>
              {label(choiceId)}
              {!wellPlaced ? (
                <span className="ml-auto text-xs text-ink-muted">
                  {t('quiz.expectedRank', { rank: item.correctOrder.indexOf(choiceId) + 1 })}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    );
  }

  if (item.type === 'MATCHING') {
    return (
      <ul className="space-y-1 text-sm">
        {item.orderedChoiceIds.map((pair) => {
          const [left, right] = pair.split(':');
          const correct = left === right;
          return (
            <li
              key={pair}
              className={cn(
                'flex flex-wrap items-center gap-2 rounded-lg px-2.5 py-1.5',
                correct ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger',
              )}
            >
              <span>{label(left ?? '')}</span>
              <span aria-hidden>→</span>
              <span>{label(right ?? '')}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  if (!question) return null;

  return (
    <ul className="space-y-1 text-sm">
      {question.choices.map((choice) => {
        const isCorrect = item.correctChoiceIds.includes(choice.id);
        const isSelected = item.selectedChoiceIds.includes(choice.id);
        return (
          <li
            key={choice.id}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2.5 py-1.5',
              isCorrect && 'bg-success/10 font-medium text-success',
              !isCorrect && isSelected && 'bg-danger/10 text-danger line-through',
              !isCorrect && !isSelected && 'text-ink-muted',
            )}
          >
            <span className="text-xs">{isCorrect ? '\u2713' : isSelected ? '\u2715' : '\u00b7'}</span>
            {language === 'en' ? choice.textEn : choice.textFr}
          </li>
        );
      })}
    </ul>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-surface-muted px-4 py-3">
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="font-display text-lg font-bold text-ink">{value}</p>
    </div>
  );
}
